import React, { useRef, useImperativeHandle, forwardRef, useState, useEffect, useMemo } from 'react';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import type { GridPageChangeEvent, GridCustomRowProps, GridCustomHeaderCellProps } from '@progress/kendo-react-grid';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import { useUploadFileMutation, useUploadStepRequiredFileMutation, useUploadDocumentMutation } from '@service/dashboardApi';
import "./style.css";

interface ColumnConfig {
    field: string;
    title: string;
    width?: number;
    cell?: React.FC<any>;
    headerCell?: React.FC<GridCustomHeaderCellProps>;
}

export interface DataGridRef {
    exportExcel: () => void;
    storeExcelToServer: () => void;
}

interface DataGridProps {
    data: any[];
    columns: ColumnConfig[];
    className?: string;
    resizable?: boolean;
    fileName?: string;
    pageable?: boolean;
    pageSize?: number;
    entityId?: number;
    isDemoLabV2?: boolean;
    step?: number;
    setIsFileUploaded?: (value: boolean) => void;
    onRowClick?: (dataItem: any) => void;
    /** When true, shows Kendo pager info (e.g. "1-10 of 32 items"). */
    showPagerInfo?: boolean;
}

const DataGrid = forwardRef<DataGridRef, DataGridProps>(({ data, columns, className, resizable = true, fileName = "", pageable = false, pageSize = 100, entityId, isDemoLabV2 = false, step, setIsFileUploaded, onRowClick, showPagerInfo = false }, ref) => {
        const excelExportRef = useRef<ExcelExport | null>(null);
        const [page, setPage] = useState({ skip: 0, take: pageSize });
        const [uploadFile] = useUploadFileMutation();
        const [uploadEntityFile] = useUploadDocumentMutation();
        const [uploadStepRequiredFile, {isSuccess: isUploadStepRequiredFileSuccess}] = useUploadStepRequiredFileMutation();

        useEffect(() => {
            if (isUploadStepRequiredFileSuccess) {
                setIsFileUploaded?.(true);
            }
        }, [isUploadStepRequiredFileSuccess, setIsFileUploaded]);

        const shouldPaginate = Boolean(pageable && data.length > pageSize);

        useEffect(() => {
            setPage((prev) => ({ ...prev, take: pageSize }));
        }, [pageSize]);

        useEffect(() => {
            setPage((prev) => (prev.skip === 0 ? prev : { ...prev, skip: 0 }));
        }, [data]);

        useEffect(() => {
            if (!shouldPaginate && page.skip !== 0) {
                setPage((prev) => ({ ...prev, skip: 0 }));
            }
        }, [shouldPaginate, page.skip]);

        useEffect(() => {
            if (!shouldPaginate) return;
            if (page.skip < data.length) return;

            const lastPageSkip =
                data.length > 0 ? Math.floor((data.length - 1) / page.take) * page.take : 0;

            setPage((prev) => ({ ...prev, skip: lastPageSkip }));
        }, [shouldPaginate, data.length, page.skip, page.take]);

        const handlePageChange = (event: GridPageChangeEvent) => {
            if (!shouldPaginate) return;
            setPage(event.page);
        };

        const pagedData = shouldPaginate
            ? data.slice(page.skip, page.skip + page.take)
            : data;

        const CustomRow = useMemo(() => {
            if (!onRowClick) return undefined;
            const handler = onRowClick;
            return ({ trProps, children, dataItem }: GridCustomRowProps) => (
                <tr {...trProps} style={{ ...trProps?.style, cursor: 'pointer' }} onClick={() => handler(dataItem)}>
                    {children}
                </tr>
            );
        }, [onRowClick]);

        const gridClassName = [className, showPagerInfo ? "show-pager-info" : ""]
            .filter(Boolean)
            .join(" ");

        useImperativeHandle(ref, () => ({
            exportExcel: () => {
                excelExportRef.current?.save();
            },

            storeExcelToServer: async () => {
                if (!fileName) return;

                const dataUrl = await excelExportRef.current?.toDataURL();
                if (!dataUrl) return;

                const base64 = dataUrl.split(",")[1];
                const binary = atob(base64);
                const byteArray = new Uint8Array(binary.length);

                for (let i = 0; i < binary.length; i++) {
                    byteArray[i] = binary.charCodeAt(i);
                }

                const blob = new Blob([byteArray], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                });

                const excelFile = new File([blob], fileName, { type: blob.type });

                if (isDemoLabV2 && entityId && excelFile) {
                    if (typeof step === "number") {
                        uploadStepRequiredFile({
                            entityId,
                            step,
                            file: excelFile,
                        });
                    } else {
                        uploadEntityFile({
                            entityId,
                            file: excelFile,
                        });
                    }
                } else if (excelFile) {
                    const formData = new FormData();
                    formData.append("file", excelFile);

                    uploadFile({
                        file: formData,
                        filename: fileName,
                    });
                }
            },
        }));
        

        return (
            <ExcelExport data={data} ref={excelExportRef} fileName={fileName}>
                <Grid
                    data={pagedData}
                    skip={shouldPaginate ? page.skip : 0}
                    take={shouldPaginate ? page.take : pagedData.length}
                    total={data.length}
                    pageable={shouldPaginate ? { info: showPagerInfo } : false}
                    onPageChange={handlePageChange}
                    className={gridClassName}
                    resizable={resizable}
                    rows={CustomRow ? { data: CustomRow } : undefined}
                >
                    {columns.map((col) => {
                        const cells =
                            col.cell || col.headerCell
                                ? {
                                      ...(col.cell ? { data: col.cell } : {}),
                                      ...(col.headerCell ? { headerCell: col.headerCell } : {}),
                                  }
                                : undefined;

                        return (
                            <GridColumn
                                key={col.field || col.title}
                                field={col.field}
                                title={col.title}
                                width={col.width}
                                cells={cells}
                            />
                        );
                    })}
                </Grid>
            </ExcelExport>
        );
    }
);

export default DataGrid;
